/* global google */
/**
 * Site-wide language selector (iter 199).
 *
 * [ITER305_INVARIANT — DO NOT REMOVE]
 *   The `pick()` function below writes the picked non-English code to
 *   `localStorage.hp_lang_explicit`, and the mount-time useEffect
 *   restores the `googtrans` cookie from that key. If either behaviour
 *   is removed, the "shudder → snap back to English" bug (fixed on
 *   03-Sep-2026) will regress across every suite domain because the
 *   inline wipe script in `public/index.html` depends on this key to
 *   distinguish "user picked a language" from "device auto-detect".
 *
 *   Companion regression test: /app/tests/iter305_translation_persist.py
 *   Companion daily watchdog:  /app/backend/hp_translate_widget_watchdog.py
 *
 *   ▸ 40 curated languages, each with its own native name + aliases
 *     so a user can type "Español", "spanish", "es", or "espn" and
 *     we still find it.
 *   ▸ The input has a rotating flag strip painted behind it — a real
 *     GIF (`/lang-keyboard.gif`) is preferred; if it 404s or the user
 *     has `prefers-reduced-motion`, we fall back to a pure-CSS flag
 *     carousel that costs 0 KB.
 *   ▸ Dropdown shows a pinned "English" row + all 39 other languages
 *     inside an internal scrollable pane. No "Show all" click required
 *     (23-Jul-2026: user asked for every language up-front).
 *   ▸ Device language is auto-applied once per session (skipped if the
 *     user has an explicit `googtrans` cookie).
 *   ▸ Public API is unchanged (`<GoogleTranslateBar activeLang onLang />`)
 *     so every site layout + sales page keeps working.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Languages, ChevronDown, Search, X, Check, User } from "lucide-react";
import { api } from "@/lib/api";
import ThemeToggle from "@/components/ThemeToggle";
import EmergencyMenu from "@/components/EmergencyMenu";
import ModulesMenu from "@/components/ModulesMenu";
import Tip from "@/components/Tip";
import ReturnToChip from "@/components/ReturnToChip";
import { isEmergencyEnabledByHost, isMeetupsHost } from "@/lib/moduleHeaderConfig";
import { getModuleHost } from "@/lib/moduleHost";

// ────────────────────────────────────────────────────────────────
//  Curated top-40 language table
//
//  `label` is the language written in its own script — this is what
//  the user sees in the dropdown. `en_name` + `aliases` fuel the
//  search auto-detect (e.g. typing "spanish" or "espn" both hit
//  Español). `flag` is a flagcdn.com country code.
// ────────────────────────────────────────────────────────────────
const LANGS = [
  { code: "en",    label: "English",           en_name: "English",       flag: "au", aliases: ["english", "en", "eng"] },
  { code: "zh-CN", label: "中文 (简体)",         en_name: "Chinese (Simplified)", flag: "cn", aliases: ["chinese", "mandarin", "cn", "zh"] },
  { code: "zh-TW", label: "中文 (繁體)",         en_name: "Chinese (Traditional)", flag: "hk", aliases: ["chinese trad", "taiwanese", "cantonese"] },
  { code: "es",    label: "Español",           en_name: "Spanish",       flag: "es", aliases: ["spanish", "espanol"] },
  { code: "ar",    label: "العربية",           en_name: "Arabic",        flag: "sa", aliases: ["arabic", "arab"] },
  { code: "hi",    label: "हिन्दी",             en_name: "Hindi",         flag: "in", aliases: ["hindi", "hin"] },
  { code: "pt",    label: "Português",         en_name: "Portuguese",    flag: "pt", aliases: ["portuguese", "portugues", "brasil"] },
  { code: "bn",    label: "বাংলা",              en_name: "Bengali",       flag: "bd", aliases: ["bengali", "bangla"] },
  { code: "ru",    label: "Русский",           en_name: "Russian",       flag: "ru", aliases: ["russian"] },
  { code: "ja",    label: "日本語",              en_name: "Japanese",      flag: "jp", aliases: ["japanese", "nihongo"] },
  { code: "pa",    label: "ਪੰਜਾਬੀ",           en_name: "Punjabi",       flag: "in", aliases: ["punjabi"] },
  { code: "de",    label: "Deutsch",           en_name: "German",        flag: "de", aliases: ["german", "deutsch"] },
  { code: "fr",    label: "Français",          en_name: "French",        flag: "fr", aliases: ["french", "francais"] },
  { code: "ko",    label: "한국어",             en_name: "Korean",        flag: "kr", aliases: ["korean", "hangul"] },
  { code: "tr",    label: "Türkçe",            en_name: "Turkish",       flag: "tr", aliases: ["turkish"] },
  { code: "vi",    label: "Tiếng Việt",        en_name: "Vietnamese",    flag: "vn", aliases: ["vietnamese", "viet"] },
  { code: "it",    label: "Italiano",          en_name: "Italian",       flag: "it", aliases: ["italian", "italiano"] },
  { code: "id",    label: "Bahasa Indonesia",  en_name: "Indonesian",    flag: "id", aliases: ["indonesian", "bahasa"] },
  { code: "th",    label: "ไทย",               en_name: "Thai",          flag: "th", aliases: ["thai"] },
  { code: "pl",    label: "Polski",            en_name: "Polish",        flag: "pl", aliases: ["polish"] },
  { code: "uk",    label: "Українська",        en_name: "Ukrainian",     flag: "ua", aliases: ["ukrainian"] },
  { code: "nl",    label: "Nederlands",        en_name: "Dutch",         flag: "nl", aliases: ["dutch", "nederlands"] },
  { code: "el",    label: "Ελληνικά",          en_name: "Greek",         flag: "gr", aliases: ["greek", "hellenic"] },
  { code: "he",    label: "עברית",             en_name: "Hebrew",        flag: "il", aliases: ["hebrew", "ivrit"] },
  { code: "sv",    label: "Svenska",           en_name: "Swedish",       flag: "se", aliases: ["swedish"] },
  { code: "no",    label: "Norsk",             en_name: "Norwegian",     flag: "no", aliases: ["norwegian"] },
  { code: "da",    label: "Dansk",             en_name: "Danish",        flag: "dk", aliases: ["danish"] },
  { code: "fi",    label: "Suomi",             en_name: "Finnish",       flag: "fi", aliases: ["finnish"] },
  { code: "cs",    label: "Čeština",           en_name: "Czech",         flag: "cz", aliases: ["czech", "cestina"] },
  { code: "ro",    label: "Română",            en_name: "Romanian",      flag: "ro", aliases: ["romanian"] },
  { code: "hu",    label: "Magyar",            en_name: "Hungarian",     flag: "hu", aliases: ["hungarian", "magyar"] },
  { code: "ta",    label: "தமிழ்",             en_name: "Tamil",         flag: "in", aliases: ["tamil"] },
  { code: "te",    label: "తెలుగు",           en_name: "Telugu",        flag: "in", aliases: ["telugu"] },
  { code: "ur",    label: "اردو",              en_name: "Urdu",          flag: "pk", aliases: ["urdu"] },
  { code: "fa",    label: "فارسی",             en_name: "Persian",       flag: "ir", aliases: ["persian", "farsi"] },
  { code: "ms",    label: "Bahasa Melayu",     en_name: "Malay",         flag: "my", aliases: ["malay"] },
  { code: "tl",    label: "Tagalog",           en_name: "Filipino",      flag: "ph", aliases: ["tagalog", "filipino"] },
  { code: "sw",    label: "Kiswahili",         en_name: "Swahili",       flag: "ke", aliases: ["swahili"] },
  { code: "af",    label: "Afrikaans",         en_name: "Afrikaans",     flag: "za", aliases: ["afrikaans"] },
  { code: "am",    label: "አማርኛ",             en_name: "Amharic",       flag: "et", aliases: ["amharic"] },
  { code: "si",    label: "සිංහල",             en_name: "Sinhala",       flag: "lk", aliases: ["sinhala", "sinhalese", "sri lankan", "sri lanka", "lk"] },
];

// Default "top 8" ordering — the world's most-spoken second-languages
// on the web. Frequency-tracker + device-language pin will override.
const DEFAULT_TOP_CODES = [
  "zh-CN", "es", "ar", "hi", "pt", "fr", "de", "ja",
];

// ────────────────────────────────────────────────────────────────
//  Helpers
// ────────────────────────────────────────────────────────────────
function loadGoogleTranslate(codes) {
  if (typeof window === "undefined") return;
  if (window.__gt_loaded__) return;
  window.__gt_loaded__ = true;
  window.googleTranslateElementInit = function () {
    const g = window.google;
    if (!g || !g.translate) return;
    new g.translate.TranslateElement(
      {
        pageLanguage: "en",
        includedLanguages: codes.join(","),
        layout: g.translate.TranslateElement.InlineLayout.SIMPLE,
        autoDisplay: false,
      },
      "google_translate_element",
    );
  };
  const s = document.createElement("script");
  s.src =
    "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  s.async = true;
  s.defer = true;
  document.body.appendChild(s);
}

function setGoogtransCookie(value) {
  const host = window.location.hostname;
  document.cookie = `googtrans=${value};path=/;`;
  document.cookie = `googtrans=${value};path=/;domain=${host};`;
  document.cookie = `googtrans=${value};path=/;domain=.${host};`;
}

function clearGoogtransCookies() {
  if (typeof document === "undefined") return;
  const host = window.location.hostname;
  const expired = "Thu, 01 Jan 1970 00:00:00 GMT";
  const variants = ["", `;domain=${host}`, `;domain=.${host}`];
  for (const d of variants) {
    document.cookie = `googtrans=;path=/${d};expires=${expired}`;
  }
  const parts = host.split(".");
  for (let i = 1; i < parts.length; i++) {
    const suffix = parts.slice(i).join(".");
    document.cookie = `googtrans=;path=/;domain=.${suffix};expires=${expired}`;
  }
}

/** Cheap Levenshtein for < 15-char strings. */
function levenshtein(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const m = [];
  for (let i = 0; i <= b.length; i++) m[i] = [i];
  for (let j = 0; j <= a.length; j++) m[0][j] = j;
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      m[i][j] =
        b[i - 1] === a[j - 1]
          ? m[i - 1][j - 1]
          : Math.min(m[i - 1][j - 1] + 1, m[i][j - 1] + 1, m[i - 1][j] + 1);
    }
  }
  return m[b.length][a.length];
}

/** Return the ranked LANGS array that matches `needle`. */
function fuzzyRank(needle) {
  if (!needle.trim()) return [];
  const q = needle.trim().toLowerCase();
  const scored = [];
  for (const l of LANGS) {
    const haystacks = [
      l.label.toLowerCase(),
      l.en_name.toLowerCase(),
      l.code.toLowerCase(),
      ...(l.aliases || []),
    ];
    let best = 999;
    for (const h of haystacks) {
      if (h.startsWith(q)) { best = 0; break; }
      if (h.includes(q))   { best = Math.min(best, 1); continue; }
      // Fuzzy only when the haystack is at least as long as the query
      // — protects short aliases like "en"/"es" from swallowing every
      // 4-letter typo (fixes "espn" → false-positive "English").
      if (/^[a-z0-9 ]+$/.test(q) && h.length >= q.length && h.length < 25) {
        const d = levenshtein(q, h.slice(0, q.length + 2));
        // Require the edit distance to be ≤ ceil(query_length / 3)
        // so 3-char queries need distance ≤ 1, 4-6 ≤ 2, 7-9 ≤ 3, etc.
        // Balances typo tolerance ("espn" → Español) without letting
        // short aliases like "en" swallow unrelated 4-letter typos.
        const threshold = Math.ceil(q.length / 3);
        if (d <= threshold) best = Math.min(best, 1 + d);
      }
    }
    if (best <= 3) scored.push([best, l]);
  }
  scored.sort((a, b) => a[0] - b[0]);
  return scored.map((x) => x[1]);
}

/** Read the frequency map from localStorage (safe on SSR). */
function readPicks() {
  try {
    const raw = localStorage.getItem("hp_lang_picks");
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function bumpPick(code) {
  try {
    const map = readPicks();
    map[code] = (map[code] || 0) + 1;
    localStorage.setItem("hp_lang_picks", JSON.stringify(map));
    return map[code];
  } catch { return 0; }
}

/** Compute the user's top-N language codes (English pinned first). */
function computeTopCodes(deviceCode) {
  const freq = readPicks();
  const scored = LANGS
    .filter((l) => l.code !== "en")
    .map((l) => {
      const boost = l.code === deviceCode ? 100 : 0;
      const seed = DEFAULT_TOP_CODES.indexOf(l.code);
      const seedScore = seed >= 0 ? (8 - seed) * 2 : 0;
      return { code: l.code, score: (freq[l.code] || 0) * 10 + boost + seedScore };
    })
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, 8).map((s) => s.code);
}

/** Best-effort detection of device language, normalised to our code set. */
function detectDeviceLang() {
  if (typeof navigator === "undefined") return null;
  const raw = (navigator.language || navigator.languages?.[0] || "").toLowerCase();
  if (!raw) return null;
  // Prefer exact match (e.g. "zh-CN") then fall back to base ("zh").
  const exact = LANGS.find((l) => l.code.toLowerCase() === raw);
  if (exact) return exact.code;
  const base = raw.split("-")[0];
  const b = LANGS.find((l) => l.code.toLowerCase().startsWith(base));
  return b ? b.code : null;
}

// ────────────────────────────────────────────────────────────────
//  Live clock (unchanged from iter197)
// ────────────────────────────────────────────────────────────────
function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30 * 1000);
    return () => clearInterval(id);
  }, []);
  const fmt = now
    .toLocaleString("en-AU", {
      weekday: "short", day: "2-digit", month: "short", year: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
    })
    .replace(",", "");
  const pretty = fmt.replace(", ", " · ").replace(",", " ·");
  return (
    <span
      className="inline-flex items-center gap-2 text-[12px] text-white/80 notranslate font-medium tabular-nums"
      data-testid="sales-clock"
    >
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#5DD39E] animate-pulse" />
      {pretty}
    </span>
  );
}

// (removed 23-Jul-2026) AnimatedFlagStrip — the animated flag ticker
// that used to sit behind the dropdown's search input. User asked to
// remove the horizontal scrolling flags. Also drops the associated
// `hpLangFlagStrip` keyframes further down.

// ────────────────────────────────────────────────────────────────
//  Main component
// ────────────────────────────────────────────────────────────────
export default function GoogleTranslateBar({
  activeLang: activeLangProp,
  onLang,
}) {
  // ─── Existing cookie parsing (preserved for URL bookmark support) ─
  const cookieLang = (() => {
    if (typeof document === "undefined") return null;
    const m = document.cookie.match(/(?:^|;\s*)googtrans=\/([^/]+)\/([^;]+)/);
    if (!m) return null;
    const code = decodeURIComponent(m[2]);
    return code && code !== "auto" ? code : null;
  })();

  // ─── Auto-detect device language on first ever visit ────────────
  // Skipped if the user already has an explicit googtrans cookie.
  const [autoLang, setAutoLang] = useState(null);
  // Signed-in user's server-side language preference (Mongo).
  const [profileLang, setProfileLang] = useState(null);
  // "You seem to prefer X — set as default?" banner. Init from
  // sessionStorage so it survives page reloads triggered by the
  // Google Translate cookie flip.
  const [suggestPref, setSuggestPref] = useState(() => {
    try {
      const raw = sessionStorage.getItem("hp_lang_suggest_pending");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });

  // Emergency menu visibility — host default (only ON for Directory /
  // Travel / Trip Finder), overridable per module via agency settings.
  // For HustleMeetups we read /api/hustlemeet-settings on mount.
  const [emergencyEnabled, setEmergencyEnabled] = useState(() => isEmergencyEnabledByHost());
  useEffect(() => {
    if (!isMeetupsHost()) return;
    let cancelled = false;
    api.get("/hustlemeet-settings")
      .then((r) => {
        if (cancelled) return;
        if (r?.data && typeof r.data.emergency_menu_enabled === "boolean") {
          setEmergencyEnabled(r.data.emergency_menu_enabled);
        }
      })
      .catch(() => { /* keep host default */ });
    return () => { cancelled = true; };
  }, []);

  // Fetch the signed-in user's preferred_language once. If set and
  // no explicit cookie exists, apply it — persists across devices.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (!mounted || !data) return;
        const pref = data.preferred_language;
        if (!pref || pref === "en") return;
        setProfileLang(pref);
        if (cookieLang) return; // user has an explicit tab-level pick
        sessionStorage.setItem("hp_lang_autoapplied", "1");
        let tries = 0;
        const iv = setInterval(() => {
          const combo = document.querySelector("select.goog-te-combo");
          if (combo) {
            combo.value = pref;
            combo.dispatchEvent(new Event("change"));
            setGoogtransCookie(`/en/${pref}`);
            clearInterval(iv);
          } else if (++tries > 30) {
            clearInterval(iv);
          }
        }, 400);
      } catch { /* not logged in, ignore */ }
    })();
    return () => { mounted = false; };
     
  }, []);
  useEffect(() => {
    // iter255: Anonymous users ALWAYS see English. Do NOT auto-apply
    //   `navigator.language` on first visit — the previous behaviour
    //   was silently flipping people to Spanish / French / Vietnamese
    //   even after they'd clicked English, because the device-language
    //   detector ran on every fresh session/incognito hit.
    //
    //   The signed-in profile-preference path above (`preferred_language`
    //   from /auth/me) is preserved: a registered user who has picked
    //   another language keeps their choice across devices. Everyone
    //   else stays in English until they manually pick from the picker.
    return;
    // eslint-disable-next-line no-unreachable
    if (cookieLang) return;
    if (sessionStorage.getItem("hp_lang_autoapplied")) return;
    const detected = detectDeviceLang();
    if (!detected || detected === "en") return;
    sessionStorage.setItem("hp_lang_autoapplied", "1");
    setAutoLang(detected);
    // Apply after Google widget loads (retry until <select.goog-te-combo> exists)
    let tries = 0;
    const iv = setInterval(() => {
      const combo = document.querySelector("select.goog-te-combo");
      if (combo) {
        combo.value = detected;
        combo.dispatchEvent(new Event("change"));
        setGoogtransCookie(`/en/${detected}`);
        clearInterval(iv);
      } else if (++tries > 30) {
        clearInterval(iv);
      }
    }, 400);
    return () => clearInterval(iv);
     
  }, []);

  const activeLang = cookieLang || activeLangProp || autoLang || "en";
  const active = LANGS.find((l) => l.code === activeLang) || LANGS[0];

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const btnRef = useRef(null);
  const inputRef = useRef(null);
  // Menu position — computed from btnRef when open. The menu itself is
  // portalled to <body> so it escapes the language-bar's overflow-x-auto
  // clipping context (which otherwise hid the dropdown entirely — user
  // report "dropdown goes sideways but nothing is visible", 23 Jul 2026).
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  // Frequency-driven "top 8" — still used by fuzzyRank sort.
  const topCodes = useMemo(
    () => computeTopCodes(detectDeviceLang()),
    // Re-compute on open so newly-picked languages surface next time.
     
    [open],
  );

  // ─── Load Google Translate widget (all 40 codes) ────────────────
  useEffect(() => {
    // iter305: If the user has an explicit pick in localStorage but no
    // googtrans cookie on this hostname yet (fresh subdomain hit, or a
    // stale wipe cleared it), restore the cookie BEFORE loading the
    // widget so Google Translate applies the translation on its first
    // pass — no manual re-select needed.
    try {
      const explicit = localStorage.getItem("hp_lang_explicit");
      if (explicit && explicit !== "en") {
        const m = document.cookie.match(/(?:^|;\s*)googtrans=\/([^/]+)\/([^;]+)/);
        const current = m ? decodeURIComponent(m[2]) : null;
        if (current !== explicit) setGoogtransCookie(`/en/${explicit}`);
      }
    } catch { /* silent */ }
    loadGoogleTranslate(LANGS.map((l) => l.code));
  }, []);

  // ─── Click-outside closes the dropdown ──────────────────────────
  // Because the menu is portalled to <body>, it lives outside `ref`.
  // We accept clicks either inside the toggle wrapper OR inside the
  // portalled menu (matched via [data-testid="sales-language-menu"]).
  //
  // iter308 (04-Feb-2026): Only attach the listener while `open` is
  //   true, and arm it one microtask after paint. The opening click
  //   (mousedown → click) was — in some Chromium + StrictMode combos —
  //   racing with the outside-click handler and snapping the panel
  //   shut instantly. The `armed` gate is the bulletproof fix. Same
  //   pattern is now used in ModulesMenu.jsx.
  useEffect(() => {
    if (!open) return;
    let armed = false;
    const armId = setTimeout(() => { armed = true; }, 0);
    const close = (e) => {
      if (!armed) return;
      if (ref.current && ref.current.contains(e.target)) return;
      const inMenu = e.target.closest?.('[data-testid="sales-language-menu"]');
      if (inMenu) return;
      setOpen(false);
      setQ("");
    };
    document.addEventListener("mousedown", close);
    return () => {
      clearTimeout(armId);
      document.removeEventListener("mousedown", close);
    };
  }, [open]);

  // ─── Focus the search input when the dropdown opens ─────────────
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  // (removed) GIF background preload — search input no longer has an
  // animated flag strip behind it (user report 23-Jul-2026: "top line
  // is 50% transparent with horizontal scrolling of the language flags,
  // please remove the scrolling"). Keeping the effect deleted rather
  // than a no-op so a stray onerror flip can't reintroduce the visual.

  // ─── Position the portalled menu under the toggle button ───────
  // Runs whenever the menu opens, and on scroll/resize while open, so
  // the dropdown tracks the anchor even when the sticky bar shifts.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const el = btnRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const menuW = 320;
      const gap = 8;
      const vw = typeof window !== "undefined" ? window.innerWidth : 360;
      // Prefer left-align to the toggle; clamp inside viewport so the
      // menu never spills off-screen on narrow phones.
      let left = r.left;
      if (left + menuW > vw - 8) left = Math.max(8, vw - menuW - 8);
      setMenuPos({ top: r.bottom + gap, left });
    };
    place();
    const opts = { passive: true };
    window.addEventListener("scroll", place, opts);
    window.addEventListener("resize", place, opts);
    return () => {
      window.removeEventListener("scroll", place, opts);
      window.removeEventListener("resize", place, opts);
    };
  }, [open]);

  // ─── Pick a language ────────────────────────────────────────────
  const pick = (code) => {
    setOpen(false);
    setQ("");
    const count = bumpPick(code);
    if (onLang) onLang(code);
    // iter305: Persist the pick in localStorage so:
    //   (a) it survives page reloads (the inline wipe script in
    //       public/index.html reads this key before wiping googtrans);
    //   (b) React can restore the googtrans cookie on subsequent
    //       first-loads if a cookie clean-up ever nukes it.
    try {
      if (code === "en") localStorage.removeItem("hp_lang_explicit");
      else localStorage.setItem("hp_lang_explicit", code);
    } catch { /* silent */ }
    // If this same non-English language has been picked 3+ times AND
    // it's not already the profile default → surface the sticky
    // suggestion banner. Persist to sessionStorage so it survives
    // Google Translate's DOM re-injection (which can trigger React
    // remounts on some browsers).
    if (
      code !== "en" &&
      count >= 3 &&
      profileLang !== code &&
      !sessionStorage.getItem(`hp_lang_dismissed_${code}`)
    ) {
      const meta = LANGS.find((l) => l.code === code);
      if (meta) {
        const s = { code, label: meta.label };
        sessionStorage.setItem("hp_lang_suggest_pending", JSON.stringify(s));
        setSuggestPref(s);
      }
    }
    const combo = document.querySelector("select.goog-te-combo");
    if (combo) {
      combo.value = code === "en" ? "" : code;
      // bubbles:true so Google Translate's delegated listener fires.
      combo.dispatchEvent(new Event("change", { bubbles: true }));
      if (code === "en") clearGoogtransCookies();
      else setGoogtransCookie(`/en/${code}`);
      return;
    }
    if (code === "en") clearGoogtransCookies();
    else setGoogtransCookie(`/en/${code}`);
    try { window.location.assign(window.location.href); }
    catch { window.location.reload(); }
  };

  const acceptPref = async () => {
    if (!suggestPref) return;
    try {
      await api.patch("/auth/me/language", {
        preferred_language: suggestPref.code,
      });
      setProfileLang(suggestPref.code);
    } catch { /* silent — user is anonymous, cookie already saved */ }
    sessionStorage.removeItem("hp_lang_suggest_pending");
    setSuggestPref(null);
  };
  const dismissPref = () => {
    if (suggestPref) {
      sessionStorage.setItem(`hp_lang_dismissed_${suggestPref.code}`, "1");
    }
    sessionStorage.removeItem("hp_lang_suggest_pending");
    setSuggestPref(null);
  };

  // ─── Search results ─────────────────────────────────────────────
  const results = q ? fuzzyRank(q) : [];
  // All non-English languages, in the curated LANGS order. English is
  // rendered separately as a pinned row above this list.
  const restOfList = LANGS.filter((l) => l.code !== "en");

  return (
    <div
      data-testid="sales-language-bar"
      className="sticky top-0 z-[70] bg-[#0E0F0E] text-[#FAF5E8] border-b border-white/10 notranslate"
    >
      {/* (removed 23-Jul-2026) hpLangFlagStrip keyframes — the search
          input no longer has a scrolling flag backdrop. Kept the outer
          <style> for future keyframe injections but empty for now. */}
      <style>{``}</style>

      <div className="max-w-7xl mx-auto pl-3 pr-2 sm:px-6 lg:px-8 py-1 flex items-center gap-2 sm:gap-3 flex-nowrap overflow-x-auto no-scrollbar text-[12px]">
        <Languages className="h-3.5 w-3.5 text-[#F5C84B] shrink-0" />
        <span className="font-medium hidden sm:inline whitespace-nowrap">Read this site in:</span>

        <div className="relative shrink-0" ref={ref}>
          <Tip label="Click here to select your language">
          <button
            ref={btnRef}
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="sales-language-toggle"
            className="inline-flex items-center gap-1 rounded-full bg-white/15 hover:bg-white/25 px-2 py-0.5 text-[12px] font-medium transition"
          >
            <img
              src={`https://flagcdn.com/w20/${active.flag}.png`}
              alt=""
              className="h-3 w-auto rounded-sm"
            />
            <span className="hidden sm:inline">{active.label}</span>
            <ChevronDown className="h-3 w-3" />
          </button>
          </Tip>

          {open && createPortal(
            <div
              role="listbox"
              data-testid="sales-language-menu"
              style={{
                position: "fixed",
                top: `${menuPos.top}px`,
                left: `${menuPos.left}px`,
                width: "320px",
                zIndex: 2147483000,
              }}
              className="rounded-xl bg-white text-[#0E0F0E] dark:bg-[#141f1b] dark:text-stone-100 shadow-2xl border border-black/10 dark:border-white/10 overflow-hidden notranslate"
            >
              {/* ─── Search input (clean, no animated backdrop) ─── */}
              <div className="border-b border-slate-200 dark:border-white/10">
                <div className="flex items-center gap-2 px-3 py-2">
                  <Search size={14} className="text-slate-500 shrink-0" />
                  <input
                    ref={inputRef}
                    data-testid="sales-lang-search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && results.length) {
                        pick(results[0].code);
                      } else if (e.key === "Escape") {
                        setOpen(false); setQ("");
                      }
                    }}
                    placeholder="Type your language…"
                    className="w-full text-[13px] bg-transparent outline-none placeholder:text-slate-500"
                    autoComplete="off"
                    spellCheck="false"
                  />
                  {/* live preview flag as user types */}
                  {q && results[0] && (
                    <img
                      data-testid="sales-lang-search-preview"
                      src={`https://flagcdn.com/w20/${results[0].flag}.png`}
                      alt=""
                      className="h-3 w-auto rounded-sm shrink-0"
                    />
                  )}
                  {q && (
                    <button
                      data-testid="sales-lang-search-clear"
                      onClick={() => { setQ(""); inputRef.current?.focus(); }}
                      className="text-slate-400 hover:text-slate-700"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>

              {/* ─── Search results ─── */}
              {q && (
                <div
                  data-testid="sales-lang-search-results"
                  className="max-h-72 overflow-auto py-1"
                >
                  {results.length === 0 && (
                    <div className="px-3 py-3 text-[13px] text-slate-500">
                      No match. Try the English name.
                    </div>
                  )}
                  {results.map((l) => (
                    <LangRow key={l.code} l={l} active={active} pick={pick} />
                  ))}
                </div>
              )}

              {/* ─── No search: pinned English + all 40 languages ─── */}
              {/* 23-Jul-2026: user asked to skip the "top 8 + Show all"
                  gate — always show every language in a scrollable pane
                  so no click is needed to reveal more. */}
              {!q && (
                <>
                  <LangRow l={LANGS[0]} active={active} pick={pick} pinned />
                  <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-400">
                    All {LANGS.length - 1} other languages
                  </div>
                  <div
                    data-testid="sales-lang-all"
                    className="max-h-[380px] overflow-auto"
                  >
                    {restOfList.map((l) => (
                      <LangRow
                        key={l.code}
                        l={l}
                        active={active}
                        pick={pick}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>,
            document.body
          )}
        </div>

        {/* Global right-aligned utility strip — Emergency + Other Modules
            + Account + clock + light/dark toggle. Every module snippet
            lives HERE only. PlatformHeader / SalesNav / module headers
            must NOT re-mount them (Rule #4).
            On mobile the clock is hidden (would blow past the viewport
            width). Utility buttons are icon-only on <sm to keep the bar
            a single 30-px tall row on phones.
            iter313d: Every icon-only control is wrapped with <Tip> so
            hover / keyboard-focus reveals the caption instantly (0 ms)
            rather than waiting on the browser's 500-1500 ms `title=`
            delay. Emergency & Modules already ship with their own text
            label so no Tip needed. */}
        <div className="ml-auto flex items-center gap-1 sm:gap-2 shrink-0">
          <ReturnToChip />
          {emergencyEnabled && (
            <EmergencyMenu
              testid="global-emergency-menu"
              className="text-[#FAF5E8] hover:bg-white/15 h-5 px-1.5 sm:px-2 text-[12px] whitespace-nowrap"
            />
          )}
          <ModulesMenu
            testid="global-modules-menu"
            className="text-[#FAF5E8] hover:bg-white/15 h-5 px-1.5 sm:px-2 text-[12px] rounded-full whitespace-nowrap"
          />
          <Tip label="Your account">
          <Link
            to="/account"
            data-testid="global-account-link"
            aria-label="Account"
            onClick={() => {
              try {
                const mh = getModuleHost();
                const ref = {
                  label: (mh && mh.title) || "HustleSuite",
                  href:  typeof window !== "undefined"
                          ? window.location.pathname + window.location.search
                          : "/",
                };
                import("@/components/AccountHeader").then((m) =>
                  m.rememberAccountReturn(ref));
              } catch (_e) { /* no-op */ }
            }}
            className="inline-flex items-center justify-center h-5 w-5 rounded-full text-[#FAF5E8] hover:bg-white/15 transition-colors shrink-0"
          >
            <User className="h-3 w-3" strokeWidth={1.5}/>
          </Link>
          </Tip>
          <span className="hidden sm:inline-flex"><LiveClock /></span>
          <Tip label="Toggle light / dark">
            <ThemeToggle
              size={12}
              testid="global-theme-toggle"
              className="!h-5 !w-5 !border-0 bg-white/15 hover:bg-white/25 text-[#FAF5E8] dark:bg-white/15 shrink-0"
            />
          </Tip>
        </div>
        <div id="google_translate_element" style={{ display: "none" }} />
      </div>

      {suggestPref && (
        <div
          data-testid="lang-pref-suggest"
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-2"
        >
          <div className="inline-flex items-center gap-3 rounded-lg bg-[#F5C84B]/15 border border-[#F5C84B]/40 px-3 py-1.5 text-[12px]">
            <span>
              You seem to prefer{" "}
              <span className="font-semibold">{suggestPref.label}</span> —
              set as your default?
            </span>
            <button
              data-testid="lang-pref-accept"
              onClick={acceptPref}
              className="inline-flex items-center gap-1 rounded-full bg-[#F5C84B] hover:bg-[#e8b737] text-[#0E0F0E] px-2.5 py-0.5 font-semibold"
            >
              <Check size={11} /> Yes
            </button>
            <button
              data-testid="lang-pref-dismiss"
              onClick={dismissPref}
              className="text-white/60 hover:text-white/90"
              aria-label="Dismiss"
            >
              <X size={12} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function LangRow({ l, active, pick, pinned }) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={l.code === active.code}
      onClick={() => pick(l.code)}
      data-testid={`sales-lang-${l.code}`}
      className={
        "w-full text-left flex items-center gap-2.5 px-3 py-2 text-[13px] hover:bg-[#F4ECD7] dark:hover:bg-white/10 " +
        (l.code === active.code ? "bg-[#FAF5E8] dark:bg-white/10 font-semibold " : "") +
        (pinned ? " border-b border-slate-100 dark:border-white/10" : "")
      }
    >
      <img
        src={`https://flagcdn.com/w20/${l.flag}.png`}
        alt=""
        className="h-3 w-auto rounded-sm"
      />
      <span>{l.label}</span>
      <span className="text-[10px] text-slate-400 ml-1">{l.en_name}</span>
      {pinned && (
        <span className="ml-auto text-[10px] text-slate-400 uppercase tracking-wider">
          Default
        </span>
      )}
      {!pinned && l.code === active.code && (
        <span className="ml-auto text-[#C05746]">✓</span>
      )}
    </button>
  );
}
